import React, { useEffect, useMemo, useState } from 'react';
import { useForm, useFieldArray, FormProvider } from 'react-hook-form';
import Get from '../../../../api/internal/Get';
import classes from './CreateBill.module.css';
import PostBody from '../../../../api/internal/PostBody';
import View from '../../../../helpers/slab/View';
import ViewPayable from '../../../../features/views/payable/Payable';
import Decimal from 'decimal.js';
import { useSearchParams } from 'react-router-dom';
import useChartOfAccountTypeOptions, {
  ChartOfAccountType,
} from '../../../../hooks/useChartOfAccountTypeOptions';
import BillInfoSection from './BillInfoSection';
import LineItemsTable from './LineItemsTable';

export interface Bill {
  vendorId: number;
  billName: string;
  billDate: string;
  dueDate: string;
  note?: string;
  lineItems: LineItem[];
}

export interface LineItem {
  chartOfAccountId?: number;
  note?: string;
  loadRentalType?: string;
  loadRentalId?: string;
  loadId?: string | number;
  rentalId?: string | number;
  assetId?: number | string;
  rate: number | null;
  quantity: number | null;
  total: number | null;
}

function CreateBill() {
  const [vendors, setVendors] = useState<VendorOptionWithPaymentTerm[]>([]);
  const [assets, setAssets] = useState<AssetOption[]>([]);
  const [searchParams] = useSearchParams();
  const accountTypes: ChartOfAccountType[] = useMemo(
    () => ['expense', 'asset', 'liability'],
    [],
  );
  const { chartOfAccountOptions } = useChartOfAccountTypeOptions({
    accountType: accountTypes,
  });

  // define form methods to be passed to FormProvider
  const methods = useForm<Bill>({
    defaultValues: {
      vendorId: null,
      billName: '',
      billDate: '',
      dueDate: '',
      note: '',
      lineItems: [
        {
          chartOfAccountId: null,
          note: '',
          loadRentalType: '',
          loadRentalId: null,
          assetId: null,
          rate: null,
          quantity: null,
          total: null,
        },
      ],
    },
  });

  const { control, handleSubmit, watch, setValue, reset } = methods;

  // setup field array for line items
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'lineItems',
  });

  useEffect(() => {
    Get('/Vendor/GetVendorList?includePaymentTerms=true').then(response => {
      if (response) {
        setVendors(response.data);
      }
    });
    Get('/Asset/GetAssetList').then(response => {
      if (response) {
        setAssets(response.data);
      }
    });
  }, []);

  // load any saved draft bill data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('createBillDraft');
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);

        // recalculate totals for each line item
        if (parsed.lineItems) {
          parsed.lineItems = parsed.lineItems.map(item => ({
            ...item,
            total: new Decimal(item.rate || 0)
              .times(item.quantity || 0)
              .toNumber()
              .toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              }),
          }));
        }

        Object.keys(parsed).forEach(key => {
          setValue(key as keyof Bill, parsed[key]);
        });
      } catch (error) {
        console.error('Failed to load saved form data:', error);
      }
    }
  }, [setValue]);

  // duplicate existing bill if "duplicateFrom" param is present
  useEffect(() => {
    const duplicateVendorAssignmentId = searchParams.get('duplicateFrom');

    const allSelectOptionsLoaded =
      vendors.length > 0 &&
      assets.length > 0 &&
      chartOfAccountOptions.length > 0;

    if (duplicateVendorAssignmentId && allSelectOptionsLoaded) {
      Get(`/Accounting/GetPayableSummary/${duplicateVendorAssignmentId}`).then(
        response => {
          if (response) {
            const billData: PayableSummary = response.data;
            
            //per odi, when duplicating a bill, leave dates, bill name, bill note, and amounts empty
            setValue('vendorId', billData.vendorId, { shouldValidate: true });
            setValue('billName', '');
            setValue('billDate', '');
            setValue('dueDate', '');
            setValue('note', '');
            const duplicatedLineItems = billData.lineItems.map(
              (item, index) => ({
                id: index,
                chartOfAccountId: item.chartOfAccountId,
                note: item.note || '',
                loadRentalType: item.loadId
                  ? 'Load'
                  : item.rentalId
                  ? 'Rental'
                  : '',
                loadRentalId:
                  item.loadId?.toString() || item.rentalId?.toString() || '',
                assetId: item.assetId || null,
                rate: null,
                quantity: null,
                total: null,
              }),
            );

            replace(duplicatedLineItems);
          }
        },
      );
    }
  }, [searchParams, setValue, replace, vendors, assets, chartOfAccountOptions]);

  // save draft 1 second after every input change
  useEffect(() => {
    const subscription = watch(data => {
      const saveTimeout = setTimeout(() => {
        localStorage.setItem('createBillDraft', JSON.stringify(data));
      }, 1000);
      return () => clearTimeout(saveTimeout);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  const onSubmit = async (data: Bill) => {
    // transform line items to replace loadRentalId with loadId or rentalId
    const transformedItems = data.lineItems.map(item => {
      if (item.loadRentalType === 'Load' && item.loadRentalId) {
        const { loadRentalType, loadRentalId, ...rest } = item;
        return {
          ...rest,
          loadId: Number(loadRentalId) || null,
        };
      } else if (item.loadRentalType === 'Rental' && item.loadRentalId) {
        const { loadRentalType, loadRentalId, ...rest } = item;
        return {
          ...rest,
          rentalId: Number(loadRentalId) || null,
        };
      }
      return item;
    });
    const bill: Bill = {
      ...data,
      lineItems: transformedItems,
    };

    PostBody('Accounting/CreateBill', bill).then(response => {
      if (response) {
        //on success reset form and clear draft
        reset();
        localStorage.removeItem('createBillDraft');
        View(
          <ViewPayable
            vendorAssignmentId={response.data}
            billName={data.billName}
          />,
        );
      }
    });
  };

  return (
    <FormProvider {...methods}>
      <form className={classes.pageContainer} onSubmit={handleSubmit(onSubmit)}>
        <BillInfoSection vendors={vendors} />
        <LineItemsTable
          fields={fields}
          remove={remove}
          append={append}
          expenseChartOfAccounts={chartOfAccountOptions}
          assets={assets}
        />
      </form>
    </FormProvider>
  );
}

export default CreateBill;
